import React from 'react';
import type { ModuleDefinition } from './moduleRegistry';
import type {
    ReportData,
    GstRevenueProps,
    GstRevenueChartPoint,
    GstRevenueBarItem,
    GstRevenueMultiLine,
    GstRevenueStackedBar,
} from '../data/types';
import { ScreenSection, ScreenDivider } from '../primitives/ScreenPrimitives';
import {
    DASH,
    chartCard,
    chartColors,
    fmtLacLabel,
    ScreenStatStrip,
    ScreenChartCard,
    ScreenLegend,
    ScreenLineChart,
    ScreenGrowthBars,
    ScreenHBars,
    ScreenStackedBars,
    ScreenDualAreaChart,
    ScreenMonthTable,
} from '../primitives/GstChartPrimitives';
import { coverFonts, ptToPx, space } from '../data/designTokens';

// ════════════════════════════════════════════════════════════════
//  GST Revenue — Section 06.  Stats + GSTR-3B table + charts +
//  B2B Customer Summary.  8px DM Mono for 12-month exact amounts.
//  Chart components and table cell builders live in
//  primitives/GstChartPrimitives (shared with GstExpenses).
// ════════════════════════════════════════════════════════════════

const SF = coverFonts.screen;

// ── Value formatting helpers ────────────────────────────────────
const fmtLacCr = (val: number): string => {
    const abs = Math.abs(val);
    if (abs >= 100) return `₹${(val / 100).toFixed(2)} Cr`;
    return `₹${val.toFixed(2)} L`;
};
// Segment/type breakdown rows for the HBars panel — replaces the monthly
// chart, which was unreadable when one segment dominates. Each row shows
// the segment's period total and share; bar length is relative to the
// biggest segment (same convention as the states breakdown).
const seriesToBarItems = (series: GstRevenueMultiLine[]): GstRevenueBarItem[] => {
    // Coerce each point to a finite, non-negative number before summing — a NaN
    // point would otherwise propagate into the total, the percent label and the
    // bar length, breaking the LIVE ScreenHBars render (skill rule 25).
    const finite = (v: number) => (Number.isFinite(v) && v > 0 ? v : 0);
    const totals = series.map((s) => s.points.reduce((a, p) => a + finite(p.value), 0));
    const grand = totals.reduce((a, b) => a + b, 0) || 1;
    const maxVal = Math.max(...totals, 1);
    const clampPct = (n: number) => Math.min(100, Math.max(0, Math.round(n)));
    return series
        .map((s, i) => ({
            label: s.label,
            value: `${fmtLacLabel(totals[i])} · ${clampPct((totals[i] / grand) * 100)}%`,
            percent: clampPct((totals[i] / maxVal) * 100),
            raw: totals[i],
        }))
        .sort((a, b) => (b.raw ?? 0) - (a.raw ?? 0));
};
const avgPts = (pts: GstRevenueChartPoint[]): number => (pts.length ? pts.reduce((s, p) => s + p.value, 0) / pts.length : 0);
const dateRange = (pts: GstRevenueChartPoint[]): string => (pts.length >= 2 ? `${pts[0].label} – ${pts[pts.length - 1].label}` : '');
const avgGrowthPct = (pts: GstRevenueChartPoint[]): string => {
    if (!pts.length) return '0%';
    const avg = pts.reduce((s, p) => s + p.value, 0) / pts.length;
    return `${avg >= 0 ? '+' : ''}${avg.toFixed(1)}%`;
};
// Average recurring share — per-month recurring/(recurring+nonRecurring),
// matching the percentages shown on the stacked bars (the raw `recurring`
// field is an amount in ₹ Lacs, not a percentage).
const avgRecurringPct = (data: GstRevenueStackedBar[]): string => {
    const pcts = data.filter((d) => d.recurring + d.nonRecurring > 0).map((d) => (d.recurring / (d.recurring + d.nonRecurring)) * 100);
    if (!pcts.length) return '0%';
    return `${Math.round(pcts.reduce((s, p) => s + p, 0) / pcts.length)}%`;
};
// Weighted median over the cohort buckets — each point's value is a customer
// COUNT, its label the age bucket. Walks the cumulative count to the bucket
// holding the middle customer and reports that bucket's label.
const medianCohortBucket = (pts: GstRevenueChartPoint[]): string => {
    const total = pts.reduce((s, p) => s + p.value, 0);
    if (total <= 0) return DASH;
    let cum = 0;
    for (const p of pts) {
        cum += p.value;
        if (cum >= total / 2) return p.label;
    }
    return pts[pts.length - 1].label;
};
const topSegmentPct = (series: GstRevenueMultiLine[]): string => {
    if (!series.length) return '';
    const totals = series.map((s) => ({ label: s.label, sum: s.points.reduce((a, p) => a + p.value, 0) }));
    const grand = totals.reduce((a, t) => a + t.sum, 0) || 1;
    const top = totals.reduce((best, t) => (t.sum > best.sum ? t : best), totals[0]);
    return `${Math.round((top.sum / grand) * 100)}%`;
};
const totalSeries = (series: GstRevenueMultiLine[]): string => {
    const sum = series.reduce((a, s) => a + s.points.reduce((b, p) => b + p.value, 0), 0);
    return fmtLacCr(sum);
};
const existingPct = (existing: GstRevenueChartPoint[], newD: GstRevenueChartPoint[]): string => {
    const exSum = existing.reduce((a, p) => a + p.value, 0);
    const newSum = newD.reduce((a, p) => a + p.value, 0);
    return `${Math.round((exSum / (exSum + newSum || 1)) * 100)}%`;
};

const getData = (data: ReportData): GstRevenueProps | undefined => data.gstRevenue;

// ════════════════════════════════════════════════════════════════
//                            S C R E E N
// ════════════════════════════════════════════════════════════════

const ScreenHsnDonut: React.FC<{ items: GstRevenueProps['hsnCategories']; total?: number }> = ({ items, total }) => {
    if (!items || items.length === 0) return null;
    const r = 38;
    const cx = 50;
    const cy = 50;
    const circumference = 2 * Math.PI * r;
    let offset = 0;
    return (
        <div style={{ display: 'flex', gap: ptToPx(7.5), marginTop: ptToPx(4.5) }}>
            <svg viewBox="0 0 100 100" width={90} height={90}>
                <circle cx={cx} cy={cy} r={r} fill="none" stroke="#E8E8E8" strokeWidth={12} />
                {items.map((item, i) => {
                    const dash = (item.percent / 100) * circumference;
                    const el = (
                        <circle
                            key={i}
                            cx={cx}
                            cy={cy}
                            r={r}
                            fill="none"
                            stroke={item.color}
                            strokeWidth={12}
                            strokeDasharray={`${dash} ${circumference}`}
                            strokeDashoffset={-offset}
                            transform={`rotate(-90 ${cx} ${cy})`}
                        />
                    );
                    offset += dash;
                    return el;
                })}
                <text x={cx} y={cy + 4} fontSize={12} fill="#1a1a1a" textAnchor="middle" fontWeight={600}>
                    {total ?? 0}
                </text>
            </svg>
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                {items.map((item, i) => (
                    <div
                        key={i}
                        style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            fontSize: ptToPx(6.75),
                            color: '#555555',
                            padding: `${ptToPx(1.5)}px 0`,
                            borderBottom: '1px solid #f8f8f8',
                        }}
                    >
                        <div style={{ display: 'flex', alignItems: 'center', gap: ptToPx(3.75) }}>
                            <div style={{ width: ptToPx(4.5), height: ptToPx(4.5), borderRadius: '50%', background: item.color }} />
                            <span>{item.name}</span>
                        </div>
                        <span style={{ fontFamily: SF.mono, fontWeight: 500, color: '#333333' }}>{item.percent}%</span>
                    </div>
                ))}
            </div>
        </div>
    );
};
const ScreenCohortBars: React.FC<{ data: GstRevenueChartPoint[] }> = ({ data }) => {
    const w = 620;
    const h = 100;
    if (data.length === 0) return null;
    const maxVal = Math.max(...data.map((d) => d.value), 1);
    const barW = (w - 60) / data.length - 4;
    return (
        <svg viewBox={`0 0 ${w} ${h}`} style={{ width: '100%', display: 'block', marginTop: ptToPx(6) }}>
            {data.map((d, i) => {
                const x = 40 + i * ((w - 80) / data.length);
                const barH = (d.value / maxVal) * (h - 25);
                const y = h - 12 - barH;
                return (
                    <g key={i}>
                        <rect x={x} y={y} width={barW} height={barH} rx={2} fill={chartColors.purple} />
                        <text x={x + barW / 2} y={y - 4} fontSize={7} fill={chartColors.purple} textAnchor="middle" fontWeight={600}>
                            {d.value}
                        </text>
                        <text x={x + barW / 2} y={h - 2} fontSize={7} fill="#aaa" textAnchor="middle">
                            {d.label}
                        </text>
                    </g>
                );
            })}
        </svg>
    );
};

// ── Screen Chart Pages ──────────────────────────────────────────
const ScreenChartsPage2: React.FC<{ d: GstRevenueProps }> = ({ d }) => {
    const m = d.chartMeta ?? {};
    const trendAvg = d.revenueTrend ? fmtLacCr(avgPts(d.revenueTrend)) : DASH;
    const trendRange = d.revenueTrend ? dateRange(d.revenueTrend) : '';
    const growthAvg = d.revenueGrowth ? avgGrowthPct(d.revenueGrowth) : DASH;
    const stateCount = d.stateBreakdown ? `${d.stateBreakdown.length} States` : DASH;
    const top3Pct =
        d.stateBreakdown && d.stateBreakdown.length >= 3
            ? `Top 3 states = ${d.stateBreakdown.slice(0, 3).reduce((s, it) => s + it.percent, 0)}% of B2B revenue`
            : 'State-wise B2B revenue split';
    const recurAvg = d.recurringCustomers ? avgRecurringPct(d.recurringCustomers) : DASH;
    const recurRange = d.recurringCustomers ? dateRange(d.recurringCustomers.map((r) => ({ label: r.label, value: r.recurring }))) : '';
    return (
        <div style={{ marginTop: ptToPx(space.sp8) }}>
            <ScreenDivider />
            <div style={{ display: 'flex', gap: ptToPx(chartCard.gridGap), marginTop: ptToPx(space.sp6) }}>
                <ScreenChartCard
                    title="Revenue"
                    value={m.revenue?.primaryText ?? trendAvg}
                    sub={m.revenue?.secondaryText ?? `Avg. revenue from ${trendRange}`}
                >
                    {d.revenueTrend ? <ScreenLineChart data={d.revenueTrend} /> : null}
                    <ScreenLegend items={[{ label: 'Revenue (₹ Lacs)', color: chartColors.ink }]} />
                </ScreenChartCard>
                <ScreenChartCard
                    title="Revenue Growth"
                    value={m.revenueGrowth?.primaryText ?? growthAvg}
                    sub={m.revenueGrowth?.secondaryText ?? 'Average MoM growth'}
                >
                    {d.revenueGrowth ? <ScreenGrowthBars data={d.revenueGrowth} /> : null}
                    <ScreenLegend
                        items={[
                            { label: 'Positive', color: chartColors.pos },
                            { label: 'Negative', color: chartColors.neg },
                        ]}
                    />
                </ScreenChartCard>
            </div>
            <div style={{ display: 'flex', gap: ptToPx(chartCard.gridGap), marginTop: ptToPx(chartCard.gridGap) }}>
                <ScreenChartCard
                    title="Domestic B2B Revenue Across States"
                    value={m.states?.primaryText ?? stateCount}
                    valueSmall
                    sub={m.states?.secondaryText ?? top3Pct}
                >
                    {d.stateBreakdown ? <ScreenHBars items={d.stateBreakdown} /> : null}
                </ScreenChartCard>
                <ScreenChartCard
                    title="Split of Recurring B2B Customers"
                    value={m.recurring?.primaryText ?? recurAvg}
                    valueSmall
                    sub={m.recurring?.secondaryText ?? `Revenue from recurring · ${recurRange}`}
                >
                    {d.recurringCustomers ? <ScreenStackedBars data={d.recurringCustomers} /> : null}
                    <ScreenLegend
                        items={[
                            { label: 'Recurring', color: chartColors.pos },
                            { label: 'Non Recurring', color: chartColors.posSoft },
                        ]}
                    />
                </ScreenChartCard>
            </div>
        </div>
    );
};
const ScreenChartsPage3: React.FC<{ d: GstRevenueProps }> = ({ d }) => {
    const m = d.chartMeta ?? {};
    const conc = d.customerConcentration;
    const avgRevCust = d.avgRevenuePerCustomer ? fmtLacCr(avgPts(d.avgRevenuePerCustomer)) : DASH;
    const avgRevRange = d.avgRevenuePerCustomer ? dateRange(d.avgRevenuePerCustomer) : '';
    // raw values are in ₹ Lacs; percent is only the bar width vs the largest
    const top5Val = d.topCustomers ? fmtLacCr(d.topCustomers.reduce((s, it) => s + (it.raw ?? 0), 0)) : DASH;
    const top5Pct = 'Combined revenue of top customers';
    return (
        <div style={{ marginTop: ptToPx(space.sp6) }}>
            <div style={{ display: 'flex', gap: ptToPx(chartCard.gridGap) }}>
                <ScreenChartCard
                    title="Avg Revenue Per B2B Customer"
                    value={m.avgRevenue?.primaryText ?? avgRevCust}
                    valueSmall
                    sub={m.avgRevenue?.secondaryText ?? `Average per customer · ${avgRevRange}`}
                >
                    {d.avgRevenuePerCustomer ? <ScreenLineChart data={d.avgRevenuePerCustomer} color={chartColors.purple} /> : null}
                    <ScreenLegend items={[{ label: 'Avg Revenue Per Customer (₹ Lacs)', color: chartColors.purple }]} />
                </ScreenChartCard>
                {conc ? (
                    <ScreenChartCard
                        title="Customer Concentration (B2B)"
                        value={m.concentration?.primaryText ?? `${conc.majorPct}%`}
                        valueSmall
                        sub={m.concentration?.secondaryText ?? 'of revenue from top customers'}
                    >
                        {/* Split bar + legend — only the real percentage split from
                            the graph; counts/amounts are not sent by this API. */}
                        <div
                            style={{
                                marginTop: ptToPx(8),
                                display: 'flex',
                                height: ptToPx(6),
                                borderRadius: ptToPx(3),
                                overflow: 'hidden',
                            }}
                        >
                            <div style={{ flex: conc.majorPct || 1, background: chartColors.purple }} />
                            <div style={{ flex: conc.minorPct || 1, background: chartColors.purpleLight }} />
                        </div>
                        <div style={{ marginTop: ptToPx(8), display: 'flex', gap: ptToPx(12) }}>
                            <div style={{ flex: 1, borderLeft: `${ptToPx(2.25)}px solid ${chartColors.purple}`, paddingLeft: ptToPx(6) }}>
                                <div style={{ fontSize: ptToPx(6.75), fontFamily: SF.sans, color: '#555555' }}>
                                    {conc.majorLabel || 'Top customers'}
                                </div>
                                <div style={{ fontSize: ptToPx(8.25), fontFamily: SF.mono, fontWeight: 500, color: '#333333' }}>
                                    {conc.majorPct}% of revenue
                                </div>
                            </div>
                            <div
                                style={{
                                    flex: 1,
                                    borderLeft: `${ptToPx(2.25)}px solid ${chartColors.purpleLight}`,
                                    paddingLeft: ptToPx(6),
                                }}
                            >
                                <div style={{ fontSize: ptToPx(6.75), fontFamily: SF.sans, color: '#555555' }}>
                                    {conc.minorLabel || 'Other customers'}
                                </div>
                                <div style={{ fontSize: ptToPx(8.25), fontFamily: SF.mono, fontWeight: 500, color: '#333333' }}>
                                    {conc.minorPct}% of revenue
                                </div>
                            </div>
                        </div>
                    </ScreenChartCard>
                ) : (
                    <div style={{ flex: 1 }} />
                )}
            </div>
            {d.topCustomers ? (
                <div style={{ marginTop: ptToPx(chartCard.gridGap) }}>
                    <ScreenChartCard
                        title={`Top ${d.topCustomers.length} Revenue Sources`}
                        value={m.topCustomers?.primaryText ?? top5Val}
                        valueSmall
                        sub={m.topCustomers?.secondaryText ?? top5Pct}
                    >
                        <ScreenHBars items={d.topCustomers} />
                    </ScreenChartCard>
                </div>
            ) : null}
        </div>
    );
};
const ScreenChartsPage4: React.FC<{ d: GstRevenueProps }> = ({ d }) => {
    const m = d.chartMeta ?? {};
    const segPct = d.revenueBySegment ? topSegmentPct(d.revenueBySegment) : DASH;
    const segRange = d.revenueBySegment?.length ? dateRange(d.revenueBySegment[0].points) : '';
    const segTopLabel = d.revenueBySegment?.length
        ? d.revenueBySegment.reduce(
              (best, s) => (s.points.reduce((a, p) => a + p.value, 0) > best.points.reduce((a, p) => a + p.value, 0) ? s : best),
              d.revenueBySegment[0],
          ).label
        : '';
    const typeTotal = d.revenueByType ? totalSeries(d.revenueByType) : DASH;
    const typeRange = d.revenueByType?.length ? dateRange(d.revenueByType[0].points) : '';
    const exPct = d.existingVsNew ? existingPct(d.existingVsNew.existing, d.existingVsNew.new) : DASH;
    const exRange = d.existingVsNew ? dateRange(d.existingVsNew.existing) : '';
    const cohortMedian = d.customerCohort ? medianCohortBucket(d.customerCohort) : DASH;
    return (
        <div style={{ marginTop: ptToPx(space.sp6) }}>
            <div style={{ display: 'flex', gap: ptToPx(chartCard.gridGap) }}>
                {d.revenueBySegment ? (
                    <ScreenChartCard
                        title="Revenue By Segment"
                        value={m.segment?.primaryText ?? segPct}
                        valueSmall
                        sub={m.segment?.secondaryText ?? `${segTopLabel} contribution · ${segRange}`}
                    >
                        <ScreenHBars items={seriesToBarItems(d.revenueBySegment)} />
                    </ScreenChartCard>
                ) : (
                    <div style={{ flex: 1 }} />
                )}
                {d.revenueByType ? (
                    <ScreenChartCard
                        title="Revenue Type"
                        value={m.revenueType?.primaryText ?? typeTotal}
                        valueSmall
                        sub={m.revenueType?.secondaryText ?? `Total sales · ${typeRange}`}
                    >
                        <ScreenHBars items={seriesToBarItems(d.revenueByType)} />
                    </ScreenChartCard>
                ) : (
                    <div style={{ flex: 1 }} />
                )}
            </div>
            <div style={{ display: 'flex', gap: ptToPx(chartCard.gridGap), marginTop: ptToPx(chartCard.gridGap) }}>
                <ScreenChartCard
                    title="Revenue By HSN Category"
                    value={m.hsn?.primaryText ?? `${d.hsnTotalProducts ?? 0} Product(s)`}
                    valueSmall
                    sub={m.hsn?.secondaryText ?? 'Top categories by GST revenue'}
                >
                    <ScreenHsnDonut items={d.hsnCategories} total={d.hsnTotalProducts} />
                </ScreenChartCard>
                {d.existingVsNew ? (
                    <ScreenChartCard
                        title="Existing Vs New Revenue"
                        value={m.existingVsNew?.primaryText ?? exPct}
                        valueSmall
                        sub={m.existingVsNew?.secondaryText ?? `From existing customers · ${exRange}`}
                    >
                        <ScreenDualAreaChart existing={d.existingVsNew.existing} newData={d.existingVsNew.new} />
                        <ScreenLegend
                            items={[
                                { label: 'Existing', color: chartColors.pos },
                                { label: 'New', color: chartColors.posLight },
                            ]}
                        />
                    </ScreenChartCard>
                ) : (
                    <div style={{ flex: 1 }} />
                )}
            </div>
            {d.customerCohort ? (
                <div style={{ marginTop: ptToPx(chartCard.gridGap) }}>
                    <ScreenChartCard
                        title="Customer Cohort"
                        value={m.cohort?.primaryText ?? cohortMedian}
                        valueSmall
                        sub={m.cohort?.secondaryText ?? 'Median customer age'}
                    >
                        <ScreenCohortBars data={d.customerCohort} />
                        <ScreenLegend items={[{ label: 'Customers', color: chartColors.purple }]} />
                    </ScreenChartCard>
                </div>
            ) : null}
        </div>
    );
};

const ScreenGstRevenue: React.FC<{ data: ReportData; index: string }> = ({ data, index }) => {
    const d = getData(data);
    if (!d) return null;
    return (
        <ScreenSection
            kicker="Revenue"
            title="GST Revenue"
            subtitle={`Revenue from GSTR-3B & GSTR-1 filings · Source: GST Portal${d.gstin ? ` · GSTIN: ${d.gstin}` : ''}`}
            index={index}
        >
            <ScreenStatStrip stats={d.stats} />
            {/* Charts first, tables below — per design feedback */}
            {d.revenueTrend || d.revenueGrowth ? <ScreenChartsPage2 d={d} /> : null}
            {d.avgRevenuePerCustomer || d.customerConcentration ? <ScreenChartsPage3 d={d} /> : null}
            {d.revenueBySegment || d.hsnCategories || d.existingVsNew || d.customerCohort ? <ScreenChartsPage4 d={d} /> : null}
            <div style={{ marginTop: ptToPx(space.sp12) }}>
                <ScreenDivider />
                <div style={{ marginTop: ptToPx(space.sp8) }}>
                    <ScreenMonthTable
                        variant="classification"
                        title="GSTR-3B Summary"
                        firstColLabel="Classifi."
                        headers={d.monthHeaders}
                        rows={d.gstr3bRows.map((r) => ({
                            label: r.classification,
                            category: r.category,
                            bold: r.bold,
                            amounts: r.amounts,
                        }))}
                        footer="All amounts in ₹"
                    />
                </div>
            </div>
            {d.b2bCustomers.length > 0 ? (
                <div style={{ marginTop: ptToPx(space.sp12) }}>
                    <ScreenDivider />
                    <div style={{ marginTop: ptToPx(space.sp8) }}>
                        {/* Never fall back to GSTR-3B monthHeaders here — their labels
                            don't match the B2B amount keys and every cell would miss. */}
                        <ScreenMonthTable
                            variant="b2b"
                            title="B2B Customer Summary"
                            subtitle="Top customers by invoice value from GSTR-1 · Data from connected GSTs"
                            firstColLabel="Customer"
                            headers={d.b2bMonthHeaders ?? []}
                            rows={d.b2bCustomers.map((r) => ({ label: r.name, bold: r.bold, isTotal: r.isTotal, amounts: r.amounts }))}
                            footer="All amounts in ₹ · Showing top 20 customers"
                        />
                    </div>
                </div>
            ) : null}
        </ScreenSection>
    );
};

// ════════════════════════════════════════════════════════════════
//                          M O D U L E   D E F
// ════════════════════════════════════════════════════════════════

export const gstRevenueModule: ModuleDefinition = {
    meta: {
        id: 'gstRevenue',
        title: 'GST Revenue',
        description: 'GSTR-3B & GSTR-1 revenue breakdown, growth trends, customer analysis and B2B summary.',
        dataSource: 'GSTR-3B, GSTR-1, GST Portal',
    },
    hasData: (d) => !!d.gstRevenue && (d.gstRevenue.gstr3bRows.length > 0 || d.gstRevenue.b2bCustomers.length > 0),
    Screen: ScreenGstRevenue,
};
